import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ooraTrafficDataset.css";
import toast from "react-hot-toast";
import {
  getOoraTrafficDatasetList,
  fetchOoraTrafficDataset,
  removeOoraTrafficDataset,
} from "../../Redux/Actions/ooraTrafficeDatasetActions";
import OoraSideNav from "../Oora Sidebar/ooraSideNav";
import "../Oora Sidebar/ooraSideNav.css";
import { formatDate, formatSlotLabel, formatDuration, formatDistance } from "./ooraTrafficDatasetHelpers";

const LIMIT = 10;

// 0 = today, 1 = tomorrow
const FETCH_DAYS = { all: [0, 1], tomorrow: [1], today: [0] };

const OoraTrafficDataset = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ooraTrafficDatasetList, listLoading, fetchLoading, removeLoading } = useSelector(
    (state) => state.ooraTrafficDataset || {}
  );

  const list = ooraTrafficDatasetList?.data?.list || [];
  const total = ooraTrafficDatasetList?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [day, setDay] = useState("");
  const [deleteRow, setDeleteRow] = useState(null);

  // fetch popup
  const [showFetch, setShowFetch] = useState(false);
  const [fetchType, setFetchType] = useState("all");
  const [force, setForce] = useState(false);

  const fetchList = useCallback(() => {
    const body = { page, limit: LIMIT };
    if (search) body.search = search;
    if (day !== "") body.day = Number(day);
    dispatch(getOoraTrafficDatasetList(body));
  }, [dispatch, page, search, day]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const closeFetch = () => {
    if (fetchLoading) return;
    setShowFetch(false);
  };

  const handleFetch = async () => {
    const response = await dispatch(fetchOoraTrafficDataset({ days: FETCH_DAYS[fetchType], force }));

    if (response?.statusCode === 200) {
      const result = response.data || {};
      if (result.already_exists) {
        toast.success(`Added: ${(result.added_dates || []).join(", ")} · ${result.patches || 0} jam patches`);
      } else {
        toast.success(`Added: ${(result.added_dates || []).join(", ")}`);
      }
      if (result.failed?.length) {
        toast.error(`${result.failed.length} road(s) failed: ${result.failed.join(", ")}`);
      }
      setShowFetch(false);
      setPage(1);
      fetchList();
    } else {
      toast.error(response?.message || "Something went wrong");
    }
  };

  const handleRemove = async () => {
    const response = await dispatch(removeOoraTrafficDataset({ id: deleteRow._id }));
    if (response?.statusCode === 200) {
      toast.success("Traffic record removed successfully");
      setDeleteRow(null);
      if (list.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchList();
    } else {
      toast.error(response?.message || "Something went wrong");
    }
  };

  return (
    <>
      <div className="oora-layout">
        <OoraSideNav />
        <div className="oora-layout-main">
          <div className="otd-wrapper">
            <div className="otd-header">
              <div className="otd-header-left">
                <div className="otd-header-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                </div>
                <div>
                  <h1 className="otd-title">Oora Traffic Dataset</h1>
                  <p className="otd-subtitle">Noida road-wise traffic, live and forecast</p>
                </div>
              </div>
              <div className="otd-actions">
                <button type="button" className="otd-btn otd-btn--ghost" onClick={() => navigate("/oora-traffic-dataset/patches")}>
                  Jam Patches
                </button>
                <button type="button" className="otd-btn otd-btn--submit" onClick={() => setShowFetch(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Fetch Traffic Data
                </button>
              </div>
            </div>

            <div className="otd-card">
              <div className="otd-toolbar">
                <div className="otd-toolbar-left">
                  <div className="otd-search">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search by road, from or to"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                  <select
                    className="otd-input otd-date-filter"
                    value={day}
                    title="Filter by day"
                    onChange={(e) => {
                      setPage(1);
                      setDay(e.target.value);
                    }}
                  >
                    <option value="">All days</option>
                    <option value="0">Today</option>
                    <option value="1">Tomorrow</option>
                  </select>
                </div>
                <span className="otd-count">{total} record{total === 1 ? "" : "s"}</span>
              </div>

              <div className="otd-table-wrap">
                <table className="otd-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th>Road</th>
                      <th>Route</th>
                      <th style={{ width: 130 }}>Date</th>
                      <th style={{ width: 110 }}>Slot (IST)</th>
                      <th style={{ width: 110 }}>Traffic</th>
                      <th style={{ width: 110 }}>Delay</th>
                      <th style={{ width: 100 }}>Distance</th>
                      <th style={{ width: 120, textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading ? (
                      <tr><td colSpan={9} className="otd-empty">Loading...</td></tr>
                    ) : list.length === 0 ? (
                      <tr><td colSpan={9} className="otd-empty">No traffic data found</td></tr>
                    ) : (
                      list.map((row, i) => (
                        <tr key={row._id}>
                          <td className="otd-mono">{(page - 1) * LIMIT + i + 1}</td>
                          <td className="otd-row-title">{row.road_name}</td>
                          <td>{row.from?.name} → {row.to?.name}</td>
                          <td>{formatDate(row.date_label)}</td>
                          <td>{formatSlotLabel(row.slot)}</td>
                          <td>
                            <span className={`otd-level otd-level--${row.traffic_level}`}>{row.traffic_level}</span>
                          </td>
                          <td>{formatDuration(row.delay_sec)}</td>
                          <td>{formatDistance(row.distance_meters)}</td>
                          <td style={{ textAlign: "right" }}>
                            <div className="otd-actions">
                              <button
                                type="button"
                                className="otd-icon-btn"
                                title="View"
                                onClick={() => navigate(`/oora-traffic-dataset/view/${row._id}`)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="otd-icon-btn otd-icon-btn--danger"
                                title="Remove"
                                onClick={() => setDeleteRow(row)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6" />
                                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="otd-pagination">
                <span className="otd-page-info">Page {page} of {totalPages}</span>
                <div className="otd-page-btns">
                  <button
                    type="button"
                    className="otd-btn otd-btn--ghost"
                    disabled={page <= 1 || listLoading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="otd-btn otd-btn--ghost"
                    disabled={page >= totalPages || listLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* FETCH POPUP */}
          {showFetch && (
            <div className="otd-overlay" onMouseDown={closeFetch}>
              <div className="otd-modal otd-modal--md" onMouseDown={(e) => e.stopPropagation()}>
                <div className="otd-modal-head">
                  <div className="otd-section-header">
                    <span className="otd-section-badge">NEW</span>
                    <h2 className="otd-section-title">Fetch Traffic Data</h2>
                  </div>
                </div>
                <div className="otd-modal-body">
                  <div className="otd-field">
                    <label className="otd-label">Fetch</label>
                    <select
                      className="otd-input"
                      value={fetchType}
                      disabled={fetchLoading}
                      onChange={(e) => setFetchType(e.target.value)}
                    >
                      <option value="all">Today + tomorrow</option>
                      <option value="tomorrow">Tomorrow only</option>
                      <option value="today">Today only</option>
                    </select>
                  </div>
                  <p className="otd-hint">
                    Fetches traffic for whole Noida. Dates already saved are skipped and old data is never
                    removed. This can take a while.
                  </p>
                  <label className="otd-check">
                    <input
                      type="checkbox"
                      checked={force}
                      disabled={fetchLoading}
                      onChange={(e) => setForce(e.target.checked)}
                    />
                    Refresh dates that are already saved (needed to get patches for old data)
                  </label>
                </div>
                <div className="otd-modal-foot">
                  <button type="button" className="otd-btn otd-btn--ghost" onClick={closeFetch} disabled={fetchLoading}>
                    Cancel
                  </button>
                  <button type="button" className="otd-btn otd-btn--submit" onClick={handleFetch} disabled={fetchLoading}>
                    {fetchLoading ? "Fetching..." : "Fetch Data"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* REMOVE CONFIRM */}
          {deleteRow && (
            <div className="otd-overlay" onMouseDown={() => !removeLoading && setDeleteRow(null)}>
              <div className="otd-modal otd-modal--sm" onMouseDown={(e) => e.stopPropagation()}>
                <div className="otd-modal-head">
                  <h2 className="otd-section-title">Remove Traffic Record</h2>
                </div>
                <div className="otd-modal-body">
                  <p className="otd-confirm-text">
                    Are you sure you want to remove <strong>{deleteRow.road_name}</strong> (
                    {formatDate(deleteRow.date_label)}, {formatSlotLabel(deleteRow.slot)})?
                  </p>
                </div>
                <div className="otd-modal-foot">
                  <button
                    type="button"
                    className="otd-btn otd-btn--ghost"
                    onClick={() => setDeleteRow(null)}
                    disabled={removeLoading}
                  >
                    Cancel
                  </button>
                  <button type="button" className="otd-btn otd-btn--danger" onClick={handleRemove} disabled={removeLoading}>
                    {removeLoading ? "Removing..." : "Remove"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OoraTrafficDataset;